/**
 * What may go INTO the editor: pasted HTML, a draft, a signature, what the AI
 * wrote. Parsed with DOMParser — an inert document: nothing in it runs and no
 * image loads while it is being read — and rebuilt from an allow-list, so an
 * `<img onerror>` in something copied from a web page never reaches the live
 * page, where it would run with the person's session.
 *
 * The server cleans what is SENT again (backend/client/html.js); this is the
 * guard for the ERP's own page.
 */

const ALLOWED = new Set([
  'P', 'BR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'SUB', 'SUP',
  'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'FONT', 'TABLE', 'THEAD', 'TBODY',
  'TR', 'TD', 'TH', 'HR', 'IMG', 'PRE', 'CODE',
])
const DROP = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT',
  'LINK', 'META', 'BASE', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'TITLE', 'HEAD'])
const ATTRS = {
  '*': ['style', 'align', 'title'],
  A: ['href'],
  IMG: ['src', 'alt', 'width', 'height'],
  FONT: ['color', 'face', 'size'],
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan'],
}
const STYLES = new Set([
  'color', 'background-color', 'font-size', 'font-family', 'font-weight', 'font-style',
  'text-decoration', 'text-decoration-line', 'text-align', 'margin-left', 'padding-left', 'list-style-type',
  // What a «button» link in an email is made of (the meeting's «Unirse»).
  'padding', 'border-radius', 'display',
])

function cleanStyle(value) {
  return String(value).split(';').map((rule) => {
    const cut = rule.indexOf(':')
    if (cut < 0) return null
    const name = rule.slice(0, cut).trim().toLowerCase()
    const val = rule.slice(cut + 1).trim()
    if (!STYLES.has(name) || /url\s*\(|expression|javascript:/i.test(val)) return null
    return `${name}: ${val}`
  }).filter(Boolean).join('; ')
}

function safeUrl(value, image) {
  const url = String(value).trim()
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url
  if (image && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(url)) return url
  return null
}

function copy(node, into, doc) {
  for (const child of [...node.childNodes]) {
    if (child.nodeType === 3) {
      into.appendChild(doc.createTextNode(child.textContent))
      continue
    }
    if (child.nodeType !== 1) continue
    const tag = child.tagName.toUpperCase()
    if (DROP.has(tag)) continue
    if (!ALLOWED.has(tag)) {
      // Unknown wrapper (a <section>, an <article>): keep what is inside.
      copy(child, into, doc)
      continue
    }
    const out = doc.createElement(tag.toLowerCase())
    for (const name of [...(ATTRS['*']), ...(ATTRS[tag] ?? [])]) {
      if (!child.hasAttribute(name)) continue
      let value = child.getAttribute(name)
      if (name === 'style') value = cleanStyle(value)
      if (name === 'href') value = safeUrl(value, false)
      if (name === 'src') value = safeUrl(value, true)
      if (value) out.setAttribute(name, value)
    }
    if (tag === 'A') { out.setAttribute('target', '_blank'); out.setAttribute('rel', 'noopener noreferrer') }
    copy(child, out, doc)
    into.appendChild(out)
  }
}

/** @returns {string} clean HTML */
export function sanitizeHtml(html) {
  if (!html) return ''
  const source = new DOMParser().parseFromString(String(html), 'text/html')
  const target = document.implementation.createHTMLDocument('')
  const box = target.createElement('div')
  copy(source.body, box, target)
  return box.innerHTML
}

/** Plain text as editor HTML. */
export function textToHtml(text) {
  const div = document.createElement('div')
  div.textContent = String(text ?? '')
  return div.innerHTML.replace(/\n/g, '<br>')
}
